import { useEffect, useState } from 'react';
import { diffSchedules } from '../utils/schedule';
import { parseChainTime } from '../utils/chainTime';

const SCHEDULE_MS = 20000;   // el backend lo cachea 10 s
const CLOCK_MS = 10000;      // para medir el retraso de la API
const ROTATION_MS = 300000;  // la rotación de Telos cambia cada 12 h
const CHANGE_TTL_MS = 24 * 3600 * 1000;

const readStored = (key) => {
  try { return JSON.parse(localStorage.getItem(key)) || null; } catch { return null; }
};
const writeStored = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* sin storage: solo se pierde el aviso */ }
};

/**
 * Opciones: measureLag (true) — mide el retraso de la API con su propio get_info
 * cada 10 s. Las páginas que ya consultan get_info (useMonitorData) lo pasan a
 * false y calculan el retraso con utils/chainClock.apiLag.
 *
 * Schedule de productores activo (orden de producción), el pendiente si lo hay,
 * el último cambio detectado y, en Telos, la rotación de standbys.
 *
 * El último schedule visto se guarda en localStorage por cadena, así un cambio
 * ocurrido entre visitas también se avisa.
 *
 * → { active, version, pending, change, rotation, loading, error }
 *   change   = { version, added, removed, detectedAt } | null (últimas 24 h)
 *   rotation = { out, in, outIndex, inIndex, last (Date), next (Date) } | null
 *   lagMs    = cuánto va la API por detrás del reloj (mediana de las últimas
 *              mediciones). Restándolo a Date.now() la tira coincide con el
 *              "Current producer" y los últimos bloques que muestra la página.
 */
const useProducerSchedule = (blockchainService, { measureLag = true } = {}) => {
  const [state, setState] = useState({
    active: [], version: null, pending: null, change: null, rotation: null, lagMs: 0, loading: true, error: null
  });

  const chainId = blockchainService?.chainId;
  const isTelos = blockchainService?.getChainFamily() === 'TELOS';

  useEffect(() => {
    if (!blockchainService) return undefined;
    let cancelled = false;
    const key = `chainscout:schedule:${chainId}`;
    const stored = readStored(key);
    const freshChange = (c) => (c && Date.now() - c.detectedAt < CHANGE_TTL_MS ? c : null);
    setState((s) => ({ ...s, active: [], version: null, pending: null, rotation: null, loading: true, error: null, change: freshChange(stored?.change) }));

    const loadSchedule = async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const res = await blockchainService.fetchFromChain('/get_producer_schedule');
        if (cancelled) return;
        const active = res.active || [];
        const prev = readStored(key);
        let change = freshChange(prev?.change);
        if (prev?.active?.length && (prev.version !== res.version || prev.active.join() !== active.join())) {
          const { added, removed } = diffSchedules(prev.active, active);
          if (added.length || removed.length) {
            change = { version: res.version, added, removed, detectedAt: Date.now() };
          }
        }
        writeStored(key, { version: res.version, active, change });
        setState((s) => ({ ...s, active, version: res.version, pending: res.pending || null, change, loading: false, error: null }));
      } catch (err) {
        if (!cancelled) setState((s) => ({ ...s, loading: false, error: err.message }));
      }
    };

    const loadRotation = async () => {
      if (!isTelos || (typeof document !== 'undefined' && document.hidden)) return;
      try {
        const res = await blockchainService.fetchFromChain('/get_table_rows', {
          code: 'eosio', scope: 'eosio', table: 'rotations', json: true, limit: 1
        });
        const r = res.rows?.[0];
        if (cancelled || !r) return;
        setState((s) => ({
          ...s,
          rotation: {
            out: r.bp_currently_out || null,
            in: r.sbp_currently_in || null,
            outIndex: r.bp_out_index,
            inIndex: r.sbp_in_index,
            last: parseChainTime(r.last_rotation_time),
            next: parseChainTime(r.next_rotation_time)
          }
        }));
      } catch {
        /* sin rotación: la caja simplemente no se muestra */
      }
    };

    const lags = [];
    const loadLag = async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const info = await blockchainService.getInfo();
        const head = parseChainTime(info.head_block_time);
        if (cancelled || !head) return;
        lags.push(Math.max(0, Date.now() - head.getTime() - 500));
        if (lags.length > 5) lags.shift();
        const median = [...lags].sort((x, y) => x - y)[Math.floor(lags.length / 2)];
        setState((s) => ({ ...s, lagMs: median }));
      } catch {
        /* sin medición: se usa el reloj local */
      }
    };

    loadSchedule();
    loadRotation();
    if (measureLag) loadLag();
    const a = setInterval(loadSchedule, SCHEDULE_MS);
    const b = setInterval(loadRotation, ROTATION_MS);
    const c = measureLag ? setInterval(loadLag, CLOCK_MS) : null;
    return () => { cancelled = true; clearInterval(a); clearInterval(b); clearInterval(c); };
  }, [blockchainService, chainId, isTelos, measureLag]);

  return state;
};

export default useProducerSchedule;
